import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useLocalizedActivityData, LocalizedActivityData } from '../data/localizedData';
import { getTripsForActivity } from '../data/relationships';
import { RelatedTrips } from '../components/RelatedTrips';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { usePageTitle } from '../hooks/usePageTitle';

export const ActivityDetailsPage: React.FC = () => {
  const { activityId } = useParams<{ activityId: string }>();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'overview' | 'gallery' | 'map' | 'related'>('overview');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [showGalleryArrows, setShowGalleryArrows] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const activityData = activityId ? useLocalizedActivityData(activityId) : null;
  const relatedTrips = getTripsForActivity(activityId!);

  // Set page title based on activity data
  usePageTitle(activityData ? `${activityData.name} - La Dolce Vita` : undefined);

  // Determine if arrows are needed based on slider overflow and on resize
  useEffect(() => {
    const evaluateArrows = () => {
      const el = sliderRef.current;
      if (!el) return;
      setShowGalleryArrows(el.scrollWidth > el.clientWidth + 2);
    };
    evaluateArrows();
    window.addEventListener('resize', evaluateArrows);
    return () => window.removeEventListener('resize', evaluateArrows);
  }, [activityData?.gallery?.length]);

  // Helper function to convert medium image path to small image path
  const getSmallImagePath = (mediumPath: string) => {
    return mediumPath
      .replace('_medium.jpg', '_small.jpg')
      .replace('_medium.JPG', '_small.jpg')
      .replace('_medium.webp', '_small.webp');
  };

  // Helper function to get image name from path
  const getImageName = (path: string) => {
    const parts = path.split('/');
    return parts[parts.length - 1]
      .replace('_medium.jpg', '')
      .replace('_medium.JPG', '')
      .replace('_medium.webp', '')
      .replace(/_/g, ' ');
  };

  // Helper function to get banner image path
  const getBannerImagePath = (activityId: string) => {
    return `/activities/${activityId}/${activityId}_banner.webp`;
  };

  // Helper function to get activity name for breadcrumb based on language
  const getActivityBreadcrumbName = (activityId: string) => {
    const activityNameMap: Record<string, Record<string, string>> = {
      'horse_riding': {
        'en': 'Horse Riding',
        'it': 'Equitazione',
        'nl': 'Paardrijden'
      },
      'kayaking': {
        'en': 'Kayaking',
        'it': 'Kayak',
        'nl': 'Kajakken'
      },
      'bike_riding': {
        'en': 'Bike Riding',
        'it': 'Ciclismo',
        'nl': 'Fietsen'
      },
      'wild_life_spotting': {
        'en': 'Wild Life Spotting',
        'it': 'Avvistamento Fauna',
        'nl': 'Wildlife Spotting'
      }
    };

    const currentLanguage = localStorage.getItem('language') || 'en';
    return activityNameMap[activityId]?.[currentLanguage] || activityData?.name || activityId;
  };

  if (!activityData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('common.activityNotFound')}</h1>
            <p className="text-gray-600 mb-8">{t('common.activityNotFoundDescription')}</p>
            <Link
              to="/"
              className="bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('common.backToHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: t('common.overview') },
    { id: 'map', label: t('common.map') },
    { id: 'related', label: t('common.related') }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      {/* First part of description */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-semibold mb-4">{t('common.overview')}</h3>
        <div 
          className="text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: activityData.description }}
        />
      </div>

      {/* Image Slider (disabled per activity via carouselEnabled === false) */}
      {activityData.carouselEnabled !== false && activityData.gallery && activityData.gallery.length > 0 && (
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-xl font-semibold mb-4">{t('common.gallery')}</h3>
          <div className={`relative ${showGalleryArrows ? 'px-10 md:px-14' : 'px-0'}`}>
            {/* Left Arrow */}
            {showGalleryArrows && (
            <button 
              className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-600 hover:text-gray-800 p-2 rounded-full shadow-md transition-all duration-200"
              onClick={() => {
                const slider = document.getElementById('activity-image-slider');
                if (slider) {
                  slider.scrollBy({ left: -200, behavior: 'smooth' });
                }
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            )}

            {/* Right Arrow */}
            {showGalleryArrows && (
            <button 
              className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-600 hover:text-gray-800 p-2 rounded-full shadow-md transition-all duration-200"
              onClick={() => {
                const slider = document.getElementById('activity-image-slider');
                if (slider) {
                  slider.scrollBy({ left: 200, behavior: 'smooth' });
                }
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            )}

            <div 
              id="activity-image-slider"
              className="flex space-x-4 overflow-x-auto pb-4 px-0" 
              ref={sliderRef}
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                ...({ WebkitScrollbar: { display: 'none' } } as any)
              }}
            >
              {activityData.gallery.map((imagePath: string, index: number) => (
                <div 
                  key={index}
                  className="flex-shrink-0 cursor-pointer group"
                  onClick={() => {
                    setSelectedImageIndex(index);
                    setIsModalOpen(true);
                  }}
                >
                  <img
                    src={getSmallImagePath(imagePath)}
                    alt={`${activityData.name} - ${getImageName(imagePath)}`}
                    className="w-72 h-48 object-cover rounded-lg shadow-md transition-transform duration-200 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
          
          {/* Creative Commons Attribution - show when credits path is provided */}
          {activityData.creditsInfoRelativePath && (
            <div className="mt-4">
              <p className="text-left text-xs text-gray-500">
                <span>{t('landing.footer.creativeCommons')}</span>
                <a 
                  href={activityData.creditsInfoRelativePath} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600 underline"
                >
                  {t('landing.footer.fullCredits')}
                </a>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Animals (structured content for wild_life_spotting) */}
      {activityId === 'wild_life_spotting' && activityData.animals && activityData.animals.length > 0 && (
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="space-y-10">
            {activityData.animals.map((animal, index) => {
              const isEven = index % 2 === 0;
              return (
                <div key={animal.id} className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                  {/* Image */}
                  <div className={`${isEven ? 'md:col-span-5 md:order-1' : 'md:col-span-5 md:order-2'}`}>
                    <img 
                      src={animal.image} 
                      alt={animal.alt} 
                      className="w-full h-64 md:h-full object-cover rounded-lg"
                    />
                  </div>
                  {/* Text */}
                  <div className={`${isEven ? 'md:col-span-7 md:order-2' : 'md:col-span-7 md:order-1'}`}>
                    <h4 className="text-xl font-semibold mb-2">{animal.name}</h4>
                    <div className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: animal.description }} />
                    {animal.travelNote && animal.travelNote.trim() && (
                      <p className="text-sm text-gray-500 mt-2">{animal.travelNote}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Creative Commons credits when credits path exists */}
          {activityData.creditsInfoRelativePath && (
            <div className="mt-4">
              <p className="text-left text-xs text-gray-500">
                <span>{t('landing.footer.creativeCommons')}</span>
                <a 
                  href={activityData.creditsInfoRelativePath} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600 underline"
                >
                  {t('landing.footer.fullCredits')}
                </a>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Second part - additional details */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('common.duration')}
            </h3>
            <p className="text-gray-600">{activityData.duration}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('common.difficulty')}
            </h3>
            <p className="text-gray-600">{activityData.difficulty}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMap = () => {
    const commonProps = {
      width: "100%",
      height: "450",
      style: { border: 0 } as React.CSSProperties,
      allowFullScreen: true as any,
      loading: "lazy" as const,
      referrerPolicy: "no-referrer-when-downgrade" as const,
      title: `${activityData!.name} - Mappa`,
    };

    const iframe = (src: string) => (
      <iframe
        src={src}
        width={commonProps.width}
        height={commonProps.height}
        style={commonProps.style}
        allowFullScreen
        loading={commonProps.loading}
        referrerPolicy={commonProps.referrerPolicy}
        title={commonProps.title}
      />
    );

    if (activityId === 'cantina_tilli') {
      return iframe('https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2958.2871971183104!2d14.28184137389609!3d42.144140674728554!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x133057df87c992ef%3A0x5021ce1b4ab5cbd5!2sAzienda%20Tilli!5e0!3m2!1sen!2snl!4v1758814185064!5m2!1sen!2snl');
    } else if (activityId === 'arrosticini_mucciante') {
      return iframe('https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2945.961875986278!2d13.742569712214731!3d42.407256831997614!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1331cdec1908b30b%3A0x661f71d2f4bdee7!2sRistoro%20Mucciante!5e0!3m2!1sen!2snl!4v1758814223654!5m2!1sen!2snl');
    } else if (activityId === 'sextantio') {
      return iframe('https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5897.952730278411!2d13.637632068995014!3d42.34302726427026!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x13302ca463b21527%3A0x185e0909e8b9ccf1!2s67020%20Santo%20Stefano%20di%20Sessanio%2C%20Province%20of%20L%27Aquila%2C%20Italy!5e0!3m2!1sen!2snl!4v1758814268576!5m2!1sen!2snl');
    } else if (activityId === 'wild_life_spotting') {
      return iframe('https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d40110.04550197701!2d13.906685282097657!3d41.78839826489455!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1330749fe8d9ccc1%3A0xc71d38a6a785c384!2s67030%20Villetta%20Barrea%2C%20Province%20of%20L%27Aquila%2C%20Italy!5e0!3m2!1sen!2snl!4v1758814331650!5m2!1sen!2snl');
    }

    return null;
  };

  const renderRelated = () => (
    <div className="space-y-6">
      <RelatedTrips 
        tripIds={relatedTrips} 
        title={t('common.includedInTrips')}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section with Banner */}
      <div className="relative h-64 md:h-96">
        <img
          src={getBannerImagePath(activityId!)}
          alt={activityData.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-2">
              {activityData.name}
            </h1>
            <p className="text-xl md:text-2xl">
              {activityData.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-gray-500 hover:text-blue-600">{t('common.home')}</Link>
            <span className="text-gray-400">/</span>
            <Link 
              to="/#activities" 
              onClick={(e) => {
                e.preventDefault();
                // Naviga alla home e poi scrolla alla sezione activities
                window.location.href = '/#activities';
              }}
              className="text-gray-500 hover:text-blue-600 cursor-pointer"
            >
              {t('destinationDetails.breadcrumbActivities')}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              {getActivityBreadcrumbName(activityId!)}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          {/* Mobile: Vertical List */}
          <div className="block sm:hidden">
            <nav className="px-4 py-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as 'overview' | 'gallery' | 'map' | 'related')}
                  className={`w-full text-left py-3 px-4 mb-2 rounded-lg font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Desktop: Horizontal Tabs */}
          <div className="hidden sm:block">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as 'overview' | 'gallery' | 'map' | 'related')}
                    className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
          
          <div className="p-6">
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'map' && (
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-6">{t('common.map')}</h3>
                <div className="rounded-lg overflow-hidden shadow-sm">
                  {renderMap()}
                </div>
              </div>
            )}
            {activeTab === 'related' && renderRelated()}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isModalOpen && activityData.carouselEnabled !== false && activityData.gallery && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative w-full h-full max-w-[96vw] max-h-[96vh] px-10 md:px-20">
            {/* Close button on the dark overlay (not over the image) */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-6 right-6 text-white hover:text-gray-300 z-20"
              aria-label="Close"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Arrows on the sides, aligned vertically and not over the image */}
            {activityData.gallery.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedImageIndex(prev => prev === 0 ? activityData.gallery.length - 1 : prev - 1)}
                  className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full z-20 transition"
                  aria-label="Previous image"
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setSelectedImageIndex(prev => prev === activityData.gallery.length - 1 ? 0 : prev + 1)}
                  className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full z-20 transition"
                  aria-label="Next image"
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Big image centered, using available space */}
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={activityData.gallery[selectedImageIndex]}
                alt={`${activityData.name} - ${getImageName(activityData.gallery[selectedImageIndex])}`}
                className="max-w-full max-h-[90vh] object-contain"
              />
            </div>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm z-20">
              {selectedImageIndex + 1} / {activityData.gallery.length}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ActivityDetailsPage; 